/**
 * BalanceSheetPrint — impression du Bilan, vue STANDARDISÉE (≠ écran) :
 * - le détail de CHAQUE agrégat est ouvert (toutes les sections dépliées en
 *   lignes de reporting ; pas les comptes du grand livre) ;
 * - Actif + Passif réunis sur UNE seule page (interligne resserré).
 * Réutilise la TABLE RÉELLE de l'écran (BsTable), mode actualsOnly, lecture seule.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import { getBsView } from '../../api/financials_bs';
import { getPlView } from '../../api/financials_pl';
import { BsTable } from '../BalanceSheetPage';
import { clipViewToClosed } from './ProfitLossPrint';
import { usePersistentValue } from '../../hooks/usePersistentState';
import PrintReportHeader from '../hr/print/PrintReportHeader';
import '../hr/print/PeoplePrint.css';
import './financialsScreenPrint.css';

const noop = () => {};
const EMPTY = new Set();

export default function BalanceSheetPrint({ activeTenant, year, editedAt, onDataReady, cutoff = null }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const [view, setView] = useState(null);
  const [netResult, setNetResult] = useState(null);
  const [lead2, setLead2] = useState(null);
  const [unit] = usePersistentValue(`fin_reports_unit_${tenantId}`, 'kchf');
  const thousands = unit !== 'chf';

  useEffect(() => {
    let alive = true;
    if (!tenantId) return undefined;
    Promise.all([
      getBsView(tenantId, year, cutoff ? { through: cutoff } : {}),
      getPlView(tenantId, year, cutoff ? { through: cutoff } : {}).catch(() => ({})),
      getBsView(tenantId, year - 1).catch(() => ({})),
    ]).then(([bs, pl, bsPrev]) => {
      if (!alive) return;
      setView(clipViewToClosed(bs.data || null));
      setNetResult((pl.data?.rows || []).find((r) => r.code === 'net_result') || null);
      // Colonne 31.12.N-2 (si l'exercice N-1 existe) : ouverture (prev_dec) de
      // la vue N-1, par code de ligne et par compte.
      const rowsP = bsPrev?.data?.rows || [];
      if (rowsP.length) {
        const byKey = {};
        rowsP.forEach((r) => {
          if (r.code != null) byKey[r.code] = Number(r.prev_dec || 0);
          (r.accounts || []).forEach((a) => { byKey[`acc:${a.account_number}`] = Number(a.prev_dec || 0); });
        });
        setLead2({ year: year - 2, byKey });
      } else setLead2(null);
    }).catch(() => {}).finally(() => { if (onDataReady) onDataReady('financials-bs'); });
    return () => { alive = false; };
  }, [tenantId, year, cutoff]); // eslint-disable-line

  if (!view) return <div className="ppl-print__section"><p>{t('common:status.loading')}</p></div>;

  // Vue standardisée : toutes les sections ouvertes (détail des agrégats), sans
  // les comptes du grand livre.
  const allSecs = new Set((view.sections || []).map((s) => s.code));

  return (
    <section className="ppl-print__section ppl-print__section--wide fin-screenprint">
      <PrintReportHeader category={p('Rapport financier')} title={p('Bilan')} year={year} unit={unit} tenant={activeTenant} editedAt={editedAt} />
      <BsTable
        view={view} thousands={thousands} plNetResult={netResult}
        unmappedCount={view.unmapped?.length || 0} actualsOnly monthsFull lead2={lead2}
        brushMode={false} activeLine={null} onPickLine={noop} onPaint={noop}
        expanded={EMPTY} onToggleLine={noop} expandedSecs={allSecs} onToggleSection={noop}
        readOnly onAssign={noop} editMode={false} onEditValue={noop} onEditForecast={noop}
        adjustMode={false} onAdjustCell={noop} notesMode={false} onEditNote={noop}
      />
    </section>
  );
}
